import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, Users, Video, Plus, Clock, ShieldCheck, CalendarPlus, Bell, BellRing, X, Heart } from "lucide-react";
import { PageHero } from "../components/FormKit";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FLAGSHIP_COURSES } from "../data/flagshipCourses";
import { toast } from "sonner";

const LIVE_TYPES = [
  { v: "product", l: "Product Live" }, { v: "course", l: "Course Live" },
  { v: "affiliate", l: "Affiliate Live" }, { v: "seller", l: "Seller Live" }, { v: "admin", l: "Admin Live" },
];

const useCountdown = (iso) => {
  const [state, setState] = useState({ label: "", status: "coming" });
  useEffect(() => {
    if (!iso) return;
    const tick = () => {
      const diff = new Date(iso) - new Date();
      if (diff <= 0) { setState({ label: "Starting now", status: "starting" }); return; }
      const d = Math.floor(diff / 86400000), h = Math.floor((diff % 86400000) / 3600000), m = Math.floor((diff % 3600000) / 60000);
      const label = d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
      setState({ label, status: diff < 1800000 ? "starting" : "coming" });
    };
    tick(); const t = setInterval(tick, 30000); return () => clearInterval(t);
  }, [iso]);
  return state;
};

export const LivePage = () => {
  const { user, authApi } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [host, setHost] = useState(null);
  const [scheduling, setScheduling] = useState(false);
  const [mySessions, setMySessions] = useState([]);
  const [filter, setFilter] = useState("live");
  const [feed, setFeed] = useState([]);
  const [replays, setReplays] = useState([]);
  const [creators, setCreators] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", scheduled_at: "", live_type: "product", category: "", cover_image: "", product_handle: "", course_slug: "" });

  const loadSessions = () => api.get("/live/sessions").then(({ data }) => setSessions(data.sessions || [])).catch(() => {});
  const loadFeed = () => { if (user) authApi.get("/live/following").then(({ data }) => setFeed(data.hosts || [])).catch(() => {}); };
  useEffect(() => { loadSessions(); const t = setInterval(loadSessions, 8000); return () => clearInterval(t); }, []);
  useEffect(() => { if (filter === "following") { loadFeed(); const t = setInterval(loadFeed, 10000); return () => clearInterval(t); } }, [filter, user]);
  useEffect(() => { if (filter === "replays") api.get("/live/replays").then(({ data }) => setReplays(data.sessions || [])).catch(() => {}); }, [filter]);
  useEffect(() => { if (filter === "top" || filter === "new") api.get(`/live/creators?sort=${filter}`).then(({ data }) => setCreators(data.creators || [])).catch(() => {}); }, [filter]);
  useEffect(() => { if (user) { authApi.get("/live/host/me").then(({ data }) => setHost(data.host)).catch(() => {}); authApi.get("/live/host/sessions").then(({ data }) => setMySessions(data.sessions || [])).catch(() => {}); } }, [user]);

  const apply = async () => { try { const { data } = await authApi.post("/live/host/apply"); setHost({ status: data.status }); toast.success("Application submitted — awaiting admin approval."); } catch { toast.error("Please sign in to apply."); } };

  const goLiveNow = async () => {
    try { const { data } = await authApi.post("/live/sessions", { title: "TAVEN Live Session" }); window.location.href = `/live/greenroom/${data.id}`; }
    catch { toast.error("Could not start."); }
  };
  const schedule = async () => {
    if (!form.title.trim() || !form.scheduled_at) { toast.error("Add a title and a date/time."); return; }
    try {
      await authApi.post("/live/sessions", { ...form, is_course: form.live_type === "course" });
      toast.success("Scheduled! It goes public once an admin approves it.");
      setScheduling(false); setForm({ title: "", description: "", scheduled_at: "", live_type: "product", category: "", cover_image: "", product_handle: "", course_slug: "" });
      loadSessions();
    } catch { toast.error("Could not schedule."); }
  };

  const live = sessions.filter((s) => s.status === "live");
  const scheduled = sessions.filter((s) => s.status === "scheduled");
  const CATS = ["Fashion", "Beauty", "Home", "Electronics", "Education", "TAVEN Signature"];
  const FILTERS = [
    { k: "live", l: "Live Now" }, { k: "following", l: "Following" }, { k: "upcoming", l: "Upcoming" },
    { k: "replays", l: "Replays" }, { k: "best", l: "Best Sellers" }, { k: "top", l: "Top Hosts" },
    { k: "new", l: "New Sellers" }, ...CATS.map((c) => ({ k: `cat:${c}`, l: c })),
  ];
  const catMatch = (s, c) => (s.category || "").toLowerCase() === c.toLowerCase();
  const isCat = filter.startsWith("cat:");
  const catName = isCat ? filter.slice(4) : null;
  const catSessions = isCat ? [...live, ...scheduled].filter((s) => catMatch(s, catName)) : [];
  const bestLive = [...live].sort((a, b) => (b.viewer_count || 0) - (a.viewer_count || 0));

  return (
    <div data-testid="live-page">
      <PageHero eyebrow="TAVEN Live Commerce" title="Live Shopping & Classes" subtitle="Watch approved TAVEN hosts present products and teach live — then shop instantly. Every product is real and checkout is secure.">
        {user ? (
          host?.status === "approved" ? (
            <div className="flex flex-wrap gap-2 items-center">
              <button onClick={goLiveNow} data-testid="go-live-btn" className="flex items-center gap-2 bg-taven-gold text-black px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-white transition-colors"><Radio className="w-4 h-4" /> Go Live Now</button>
              <button onClick={() => setScheduling(true)} data-testid="schedule-live-btn" className="flex items-center gap-2 border border-taven-gold text-taven-gold px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-taven-gold hover:text-black transition-colors"><CalendarPlus className="w-4 h-4" /> Schedule a Live</button>
            </div>
          ) : host?.status === "pending" ? (
            <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-white/10 border border-white/20 px-3 py-1.5"><Clock className="w-3.5 h-3.5 text-taven-gold" /> Host application pending review</span>
          ) : host?.status === "blocked" ? (
            <span className="text-xs font-bold uppercase tracking-wider text-red-300">Live access suspended</span>
          ) : (
            <button onClick={apply} data-testid="apply-host-btn" className="flex items-center gap-2 border border-taven-gold text-taven-gold px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-taven-gold hover:text-black transition-colors"><ShieldCheck className="w-4 h-4" /> Apply to Become a Live Host</button>
          )
        ) : (
          <Link to="/login" state={{ from: "/live" }} data-testid="live-signin-link" className="text-xs font-bold uppercase tracking-wider bg-taven-gold text-black px-4 py-2.5">Sign in to host or shop live</Link>
        )}
      </PageHero>

      <div className="border-b border-black/10 bg-white sticky top-0 z-30">
        <div className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 flex gap-1.5 overflow-x-auto no-scrollbar py-2" data-testid="live-filters">
          {FILTERS.map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`filter-${f.k.replace(":", "-").replace(/\s+/g, "-").toLowerCase()}`}
              className={`whitespace-nowrap px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-full transition-colors ${filter === f.k ? "bg-taven-black text-white" : "bg-taven-gray/50 text-black/60 hover:text-black"}`}>{f.l}</button>
          ))}
        </div>
      </div>

      <section className="py-10 px-4 md:px-8 lg:px-12 mx-auto max-w-7xl">
      {filter === "following" ? (
        <FollowingFeed feed={feed} user={user} onToggleNotify={loadFeed} />
      ) : filter === "replays" ? (
        <div data-testid="replays-view">
          <div className="flex items-center gap-2 mb-4"><Video className="w-5 h-5 text-taven-gold" /><h2 className="text-lg font-black font-display uppercase tracking-wide">Replays</h2></div>
          {replays.length === 0 ? <p className="text-sm text-muted-foreground">No replays yet — ended lives will appear here.</p> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{replays.map((s) => <SessionCard key={s.id} s={s} replay />)}</div>
          )}
        </div>
      ) : filter === "top" || filter === "new" ? (
        <div data-testid="creators-view">
          <div className="flex items-center gap-2 mb-4"><Users className="w-5 h-5 text-taven-gold" /><h2 className="text-lg font-black font-display uppercase tracking-wide">{filter === "top" ? "Top Hosts" : "New Sellers"}</h2></div>
          {creators.length === 0 ? <p className="text-sm text-muted-foreground">No creators to show yet.</p> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {creators.map((c) => (
                <Link key={c.email} to={`/creator/${encodeURIComponent(c.email)}`} data-testid={`creator-${c.email}`} className="border border-black/10 bg-white p-4 flex items-center gap-3 hover:border-taven-gold hover-lift">
                  <span className="w-11 h-11 rounded-full bg-taven-black text-taven-gold flex items-center justify-center font-black">{(c.name || "?")[0].toUpperCase()}</span>
                  <div className="min-w-0"><p className="font-bold text-sm truncate">{c.name}</p><p className="text-xs text-muted-foreground capitalize">{c.role} · {c.followers} followers</p></div>
                </Link>
              ))}
            </div>
          )}
        </div>
      ) : filter === "upcoming" ? (
        <div>
          <div className="flex items-center gap-2 mb-4"><Clock className="w-5 h-5 text-taven-gold" /><h2 className="text-lg font-black font-display uppercase tracking-wide">Upcoming Live</h2></div>
          {scheduled.length === 0 ? <p className="text-sm text-muted-foreground">No upcoming sessions scheduled yet.</p> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="upcoming-list">{scheduled.map((s) => <SessionCard key={s.id} s={s} />)}</div>
          )}
        </div>
      ) : isCat ? (
        <div data-testid={`cat-view-${catName.replace(/\s+/g, "-").toLowerCase()}`}>
          <div className="flex items-center gap-2 mb-4"><Radio className="w-5 h-5 text-taven-gold" /><h2 className="text-lg font-black font-display uppercase tracking-wide">{catName}</h2></div>
          {catSessions.length === 0 ? <p className="text-sm text-muted-foreground">No {catName} lives right now — check back soon.</p> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{catSessions.map((s) => <SessionCard key={s.id} s={s} live={s.status === "live"} />)}</div>
          )}
        </div>
      ) : (
        <>
        {/* Live Now (default) + Best Sellers */}
        <div className="flex items-center gap-2 mb-4"><Radio className="w-5 h-5 text-red-500 animate-pulse" /><h2 className="text-lg font-black font-display uppercase tracking-wide">{filter === "best" ? "Best Sellers — Live" : "Live Now"}</h2></div>
        {(filter === "best" ? bestLive : live).length === 0 ? <p className="text-sm text-muted-foreground mb-10">No live sessions right now — check Upcoming or Replays.</p> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">{(filter === "best" ? bestLive : live).map((s) => <SessionCard key={s.id} s={s} live />)}</div>
        )}

        {filter === "live" && (
          <>
            <div className="flex items-center gap-2 mb-4"><Clock className="w-5 h-5 text-taven-gold" /><h2 className="text-lg font-black font-display uppercase tracking-wide">Upcoming Live</h2></div>
            {scheduled.length === 0 ? <p className="text-sm text-muted-foreground">No upcoming sessions scheduled yet.</p> : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="upcoming-list">{scheduled.map((s) => <SessionCard key={s.id} s={s} />)}</div>
            )}
          </>
        )}
        </>
      )}

        {user && host?.status === "approved" && mySessions.length > 0 && (
          <div className="mt-12" data-testid="my-schedule">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><CalendarPlus className="w-5 h-5 text-taven-gold" /><h2 className="text-lg font-black font-display uppercase tracking-wide">My Live Schedule</h2></div>
              <Link to="/studio" data-testid="creator-studio-link" className="text-xs font-bold uppercase tracking-wider text-taven-gold hover:underline">Creator Studio →</Link>
            </div>
            <div className="border border-black/10 bg-white divide-y divide-black/5">
              {mySessions.map((s) => (
                <div key={s.id} className="flex items-center justify-between px-4 py-3" data-testid={`my-session-${s.id}`}>
                  <div>
                    <p className="font-bold text-sm">{s.title}</p>
                    <p className="text-xs text-muted-foreground">{s.scheduled_at ? new Date(s.scheduled_at).toLocaleString() : "Instant"} · <span className="capitalize">{s.live_type}</span></p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase px-2 py-1 ${s.status === "live" ? "bg-red-100 text-red-700" : s.status === "ended" ? "bg-black/10 text-black/50" : s.approval === "approved" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{s.status === "scheduled" ? (s.approval === "approved" ? "Approved" : "Pending") : s.status}</span>
                    {s.status !== "ended" && <Link to={s.status === "live" ? `/live/${s.id}` : `/live/greenroom/${s.id}`} className="text-xs font-bold text-taven-gold hover:underline">{s.status === "live" ? "Open" : "Green Room"}</Link>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {scheduling && (
        <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center px-4" onClick={() => setScheduling(false)}>
          <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="schedule-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="text-xl font-black font-display">Schedule a Live</h3><button onClick={() => setScheduling(false)}><X className="w-5 h-5" /></button></div>
            <div className="space-y-3">
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Live title" data-testid="sched-title" className="w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-taven-gold" />
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={2} className="w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-taven-gold" />
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-bold text-black/60">Date & time</label><input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="sched-datetime" className="w-full border border-black/15 px-3 py-2 text-sm outline-none focus:border-taven-gold mt-1" /></div>
                <div><label className="text-xs font-bold text-black/60">Type</label><select value={form.live_type} onChange={(e) => setForm({ ...form, live_type: e.target.value })} data-testid="sched-type" className="w-full border border-black/15 px-3 py-2 text-sm mt-1">{LIVE_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}</select></div>
              </div>
              <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Category (e.g. Jewelry, Business)" className="w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-taven-gold" />
              {form.live_type === "course" ? (
                <div>
                  <label className="text-xs font-bold text-black/60">Course for this live class</label>
                  <select value={form.course_slug} onChange={(e) => setForm({ ...form, course_slug: e.target.value })} data-testid="sched-course" className="w-full border border-black/15 px-3 py-2 text-sm mt-1">
                    <option value="">Select a course…</option>
                    {FLAGSHIP_COURSES.map((c) => <option key={c.slug} value={c.slug}>{c.title}</option>)}
                  </select>
                </div>
              ) : (
                <input value={form.product_handle} onChange={(e) => setForm({ ...form, product_handle: e.target.value })} placeholder="Pre-pin a TAVEN product handle (optional)" className="w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-taven-gold" />
              )}
              <button onClick={schedule} data-testid="sched-submit" className="w-full bg-taven-orange text-white font-bold uppercase tracking-wider py-3.5 hover:bg-[#d9561a]">Schedule Live</button>
              <p className="text-xs text-muted-foreground text-center">Scheduled lives go public only after admin approval.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FollowingFeed = ({ feed, user, onToggleNotify }) => {
  const { authApi } = useAuth();
  const toggleNotify = async (email) => {
    try { const { data } = await authApi.post(`/live/host/${encodeURIComponent(email)}/notify`); toast.success(data.notify ? "We'll alert you when they go live." : "Live alerts turned off."); onToggleNotify?.(); }
    catch { toast.error("Please sign in to get live alerts."); }
  };
  const unfollow = async (email) => {
    try { await authApi.post(`/live/host/${encodeURIComponent(email)}/follow`); onToggleNotify?.(); }
    catch { toast.error("Could not update follow."); }
  };

  if (!user) return (
    <section className="py-16 px-4 text-center" data-testid="following-signin">
      <Users className="w-10 h-10 text-taven-gold mx-auto mb-3" />
      <h2 className="text-lg font-black font-display uppercase tracking-wide mb-2">Follow your favorite hosts</h2>
      <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">Sign in to follow TAVEN sellers and creators, see when they go live, and get one-tap alerts.</p>
      <Link to="/login" state={{ from: "/live" }} className="inline-block bg-taven-gold text-black px-5 py-2.5 text-xs font-bold uppercase tracking-wider">Sign in</Link>
    </section>
  );

  if (feed.length === 0) return (
    <section className="py-16 px-4 text-center" data-testid="following-empty">
      <Heart className="w-10 h-10 text-taven-gold mx-auto mb-3" />
      <h2 className="text-lg font-black font-display uppercase tracking-wide mb-2">You're not following anyone yet</h2>
      <p className="text-sm text-muted-foreground max-w-md mx-auto">Open any live room and tap <strong>Follow</strong> to add hosts here. Followed hosts show up first when they go live.</p>
    </section>
  );

  return (
    <section className="py-10 px-4 md:px-8 lg:px-12 mx-auto max-w-7xl" data-testid="following-feed">
      <div className="flex items-center gap-2 mb-5"><Users className="w-5 h-5 text-taven-gold" /><h2 className="text-lg font-black font-display uppercase tracking-wide">Hosts You Follow</h2></div>
      <div className="space-y-6">
        {feed.map((h) => (
          <div key={h.host_email} className="border border-black/10 bg-white p-4" data-testid={`follow-host-${h.host_email}`}>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-taven-black text-taven-gold flex items-center justify-center font-black">{(h.host_name || "?")[0].toUpperCase()}</span>
                <div>
                  <p className="font-bold text-sm flex items-center gap-2">{h.host_name}{h.is_live && <span className="flex items-center gap-1 text-[10px] font-bold uppercase bg-red-600 text-white px-1.5 py-0.5"><Radio className="w-2.5 h-2.5" /> Live</span>}</p>
                  <p className="text-xs text-muted-foreground capitalize">{h.host_role} · {h.followers} follower{h.followers === 1 ? "" : "s"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggleNotify(h.host_email)} data-testid={`notify-toggle-${h.host_email}`} className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-2 border ${h.notify ? "bg-taven-gold text-black border-taven-gold" : "border-black/15 text-black/70 hover:border-taven-gold"}`}>{h.notify ? <BellRing className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}{h.notify ? "Alerts on" : "Notify when live"}</button>
                <button onClick={() => unfollow(h.host_email)} data-testid={`unfollow-${h.host_email}`} className="text-xs font-bold uppercase tracking-wider text-black/40 hover:text-red-600 px-2">Unfollow</button>
              </div>
            </div>
            {h.is_live ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{h.live.map((s) => <SessionCard key={s.id} s={s} live />)}</div>
            ) : h.upcoming.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{h.upcoming.map((s) => <SessionCard key={s.id} s={s} />)}</div>
            ) : (
              <p className="text-xs text-muted-foreground">Not live right now — we'll alert you when they go live if alerts are on.</p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

const SessionCard = ({ s, live, replay }) => {
  const cd = useCountdown(s.scheduled_at);
  const [reminded, setReminded] = useState(false);
  const [email, setEmail] = useState("");
  const [asking, setAsking] = useState(false);

  const remind = async () => {
    if (!email) { setAsking(true); return; }
    try { await api.post(`/live/sessions/${s.id}/remind`, { email }); setReminded(true); setAsking(false); toast.success("We'll remind you before it starts."); }
    catch { toast.error("Could not set reminder."); }
  };

  const body = (
    <>
      <div className="aspect-video bg-taven-black relative flex items-center justify-center overflow-hidden">
        {s.cover_image ? <img src={s.cover_image} alt="" className="w-full h-full object-cover opacity-80" /> : <Video className="w-10 h-10 text-taven-gold/60" />}
        {replay ? <span className="absolute top-2 left-2 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-black/70 text-white px-2 py-1"><Video className="w-3 h-3" /> Replay</span>
          : live ? <span className="absolute top-2 left-2 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white px-2 py-1"><Radio className="w-3 h-3" /> Live Now</span>
          : <span className={`absolute top-2 left-2 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${cd.status === "starting" ? "bg-taven-orange text-white" : "bg-taven-gold text-black"}`}><Clock className="w-3 h-3" /> {cd.status === "starting" ? "Starting Soon" : `In ${cd.label || "…"}`}</span>}
        {live && <span className="absolute bottom-2 right-2 flex items-center gap-1 text-[10px] text-white/80 bg-black/60 px-2 py-0.5"><Users className="w-3 h-3" /> {s.viewer_count || 0}</span>}
        {(s.products?.length > 0) && <span className="absolute bottom-2 left-2 flex items-center gap-1 text-[10px] text-white bg-taven-orange px-2 py-0.5 font-bold">{s.products.length} product{s.products.length !== 1 ? "s" : ""}</span>}
      </div>
      <div className="p-4">
        <p className="font-bold line-clamp-1">{s.title}</p>
        <p className="text-xs text-muted-foreground mt-1">{s.host_name} · <span className="capitalize">{s.live_type || s.host_role}</span>{s.category ? ` · ${s.category}` : ""}</p>
      </div>
    </>
  );

  if (live || replay) return <Link to={`/live/${s.id}`} data-testid={`${replay ? "replay" : "live"}-card-${s.id}`} className="bg-white border border-black/5 hover:border-taven-gold hover-lift group block">{body}</Link>;

  return (
    <div className="bg-white border border-black/5" data-testid={`upcoming-card-${s.id}`}>
      {body}
      <div className="px-4 pb-4">
        {reminded ? (
          <span className="flex items-center gap-1.5 text-xs font-bold text-green-600"><BellRing className="w-4 h-4" /> Reminder set</span>
        ) : asking ? (
          <div className="flex gap-2">
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@email.com" data-testid={`remind-email-${s.id}`} className="flex-1 border border-black/15 px-2 py-1.5 text-xs outline-none focus:border-taven-gold" />
            <button onClick={remind} data-testid={`remind-confirm-${s.id}`} className="bg-taven-black text-white text-xs font-bold uppercase px-3 hover:bg-taven-orange">Set</button>
          </div>
        ) : (
          <button onClick={remind} data-testid={`remind-btn-${s.id}`} className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-taven-gold hover:underline"><Bell className="w-3.5 h-3.5" /> Remind me</button>
        )}
      </div>
    </div>
  );
};

export default LivePage;
